import { useConsumeMagicLink } from '@/api/auth/auth.hooks.ts';
import { Box, Button, CircularProgress, Typography } from '@mui/material';
import { useEffect, useState } from 'react';
import useSignIn from 'react-auth-kit/hooks/useSignIn';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { AuthContainer } from '../components/auth-container.component';

type Status = 'pending' | 'error';

/**
 * Tokens we've already submitted, scoped to the module so StrictMode's
 * mount→unmount→remount cycle (and any back-navigation that lands here again)
 * cannot spend the same single-use token twice.
 */
const attemptedTokens = new Set<string>();

export function AuthMagicLinkPage() {
  const navigate = useNavigate();
  const signIn = useSignIn();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  // A missing token is an error from the first render — deriving it here keeps
  // the effect free of a synchronous setState.
  const [status, setStatus] = useState<Status>(token ? 'pending' : 'error');

  // Callbacks live on the hook config (not on `mutate(…, {…})`) so they fire
  // even if React StrictMode unmounts the first instance before the response
  // arrives — `mutate()` callbacks are component-scoped and would be dropped.
  const consumeMagicLink = useConsumeMagicLink({
    onSuccess: (response) => {
      if (response.accessToken) {
        signIn({
          auth: { token: response.accessToken, type: 'Bearer' },
        });
        navigate('/');
        return;
      }
      setStatus('error');
    },
    onError: () => setStatus('error'),
  });

  useEffect(() => {
    if (!token || attemptedTokens.has(token)) return;
    attemptedTokens.add(token);
    consumeMagicLink.mutate({ token });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  if (status === 'error') {
    return (
      <>
        <Typography
          variant="h5"
          sx={{
            color: '#02344C',
            fontWeight: 700,
            mb: 0.75,
            textAlign: 'center',
            letterSpacing: '-0.01em',
          }}
        >
          Anmeldung fehlgeschlagen
        </Typography>
        <Typography
          variant="body2"
          sx={{
            color: 'text.secondary',
            mb: 1,
            textAlign: 'center',
            maxWidth: 340,
            lineHeight: 1.6,
          }}
        >
          Dieser Anmeldelink ist ungültig oder abgelaufen. Bitte fordern Sie
          einen neuen an oder melden Sie sich mit Ihrem Passwort an.
        </Typography>
        <AuthContainer>
          <Button
            onClick={() => navigate('/auth')}
            fullWidth
            variant="contained"
            sx={{
              py: 1.25,
              borderRadius: 2,
              textTransform: 'none',
              fontWeight: 600,
              fontSize: '0.9rem',
            }}
          >
            Zur Anmeldung
          </Button>
        </AuthContainer>
      </>
    );
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 2,
        mt: 2,
      }}
    >
      <CircularProgress size={28} />
      <Typography
        variant="body2"
        sx={{ color: 'text.secondary', textAlign: 'center' }}
      >
        Sie werden angemeldet...
      </Typography>
    </Box>
  );
}
